import { useState } from 'react'
import { COLORS, TYPE, SPACE } from './tokens'
import SectionLabel from './SectionLabel'
import Dropzone from './Dropzone'
import AddLayerAffordance from './AddLayerAffordance'
import { extractTextFromStoragePath } from '../../shared/utils/pdfTextExtract'
import { supabase } from '../../shared/lib/supabase'

const MAX_SIZE = 25 * 1024 * 1024

async function uploadAndExtract(clientId, file) {
  if (file.type !== 'application/pdf') throw new Error('Only PDFs are supported')
  if (file.size > MAX_SIZE) throw new Error('File exceeds 25MB limit')
  const documentId = crypto.randomUUID()
  const filePath = `${clientId}/${documentId}-${file.name}`
  const { error: upErr } = await supabase.storage
    .from('client-documents')
    .upload(filePath, file, { contentType: 'application/pdf', upsert: false })
  if (upErr) throw upErr
  const { data: userRes } = await supabase.auth.getUser()
  const uploaderUserId = userRes?.user?.id
  const { data: docRow, error: dbErr } = await supabase
    .from('client_documents')
    .insert({
      client_id: clientId,
      file_name: file.name,
      file_path: filePath,
      file_size: file.size,
      mime_type: 'application/pdf',
      category: 'cyber_insurance',
      description: `Uploaded via Cyber Insurance Review · ${new Date().toISOString().slice(0,10)}`,
      uploaded_by: uploaderUserId,
      uploaded_by_role: 'admin'
    })
    .select()
    .single()
  if (dbErr) throw dbErr
  const { text: extractedText } = await extractTextFromStoragePath(supabase, 'client-documents', filePath)
  if (!extractedText || extractedText.trim().length < 100) {
    throw new Error('This PDF appears to be an image-only scan. The Specialist needs extractable text — please provide a text-based PDF.')
  }
  return { documentId: docRow.id, fileName: file.name, extractedText }
}

export default function UploadState({ clientId, onSubmit, disabled }) {
  const [primaryDoc, setPrimaryDoc] = useState(null)
  const [excessDoc, setExcessDoc] = useState(null)
  const [showExcess, setShowExcess] = useState(false)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const handlePrimary = async (file) => {
    setBusy(true); setError('')
    try {
      const doc = await uploadAndExtract(clientId, file)
      setPrimaryDoc(doc)
      if (!showExcess) {
        // Auto-run single mode immediately if the excess dropzone isn't shown
        onSubmit({ mode: 'single', documents: [doc] })
      }
    } catch (err) {
      setError(err?.message || 'Upload failed')
    } finally { setBusy(false) }
  }

  const handleExcess = async (file) => {
    setBusy(true); setError('')
    try {
      const doc = await uploadAndExtract(clientId, file)
      setExcessDoc(doc)
      if (primaryDoc) {
        onSubmit({ mode: 'portfolio', documents: [primaryDoc, doc] })
      }
    } catch (err) {
      setError(err?.message || 'Upload failed')
    } finally { setBusy(false) }
  }

  return (
    <section style={{ maxWidth: SPACE.sectionMaxWidth, margin: '0 auto', padding: `48px ${SPACE.pagePadding}` }}>
      <SectionLabel>Upload policy</SectionLabel>
      <Dropzone
        onFile={handlePrimary}
        disabled={disabled || busy}
        label={primaryDoc ? `Uploaded: ${primaryDoc.fileName}` : 'Drop a policy PDF here'}
      />
      <p style={{ ...TYPE.body, color: COLORS.mutedText, marginTop: '20px', maxWidth: '560px' }}>
        Single PDF only. Up to 25MB. The Specialist will extract the policy structure,
        evaluate coverage against your business context, and produce a review with cited findings.
      </p>
      {error && (
        <p style={{ ...TYPE.body, color: '#C04A3D', marginTop: '16px' }}>{error}</p>
      )}
      <AddLayerAffordance expanded={showExcess} onExpand={() => setShowExcess(true)} disabled={disabled || busy} />
      {showExcess && (
        <div style={{ marginTop: '32px' }}>
          <SectionLabel variant="severity">Second policy · excess layer</SectionLabel>
          <div style={{ marginTop: '16px' }}>
            <Dropzone
              onFile={handleExcess}
              disabled={disabled || busy}
              label={excessDoc ? `Uploaded: ${excessDoc.fileName}` : 'Drop the excess policy PDF here'}
            />
          </div>
        </div>
      )}
    </section>
  )
}
